import React, {useEffect, useMemo, useRef, useState} from 'react';

import {Add, Close, Delete, Done, Edit, Info, NoPhotography, QuestionMark, Search} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {Article, ArticleType, DeliveryStatus, DeliveryStatusConverter} from '../../api/caseApiTypes';
import {FetchResult} from '../../api/m3Api';
import {EnlargableImage} from '../../commonComponents/EnlargableImage';
import {useArticleImages} from '../../hooks/UseArticleImages';
import {useArticle} from '../../hooks/useArticles';
import {M3ArticleWithQuantity, M3SearchModal} from '../m3Search/M3SearchDialog';
import {CaseTextField} from './formFieldComponents/CaseTextField';
import {CaseArticleInfo} from './formFieldComponents/CaseArticleInfo';
import styles from './CaseArticles.module.css';
import {generateRandomString} from '../../helpers/StringHelpers';

import {DragDropProvider, useDroppable} from "@dnd-kit/react";
import {useSortable} from "@dnd-kit/react/sortable";

type ArticleDnD = Article & {
  dragId: string;
};

export function ArticleColumn(
  {
    children,
  }: {
    children: any,
  }) {

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(25em, 1fr))',
        gridAutoFlow: "row",
        gap: '1em',
        marginBottom: '2em'
      }}>
        {children}
      </div>
    </>
  );
}

export function DraggableArticle(
  {
    id,
    index,
    column,
    disabled,
    children,
    theme
  }: {
    id: any,
    index: any,
    column: any,
    disabled: boolean,
    children: React.ReactNode,
    theme: 'dark' | 'light'
  }) {

  const [element, setElement] = useState<Element | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);

  const {ref, isDragging} = useSortable({
    id,
    index,
    element,
    handle: handleRef,
    type: 'article',
    accept: 'article',
    group: column,
  });

  return (
    <>
      <div
        ref={setElement}
        data-shadow={isDragging || undefined}
        className={styles.dragItem}
      >
        <button
          ref={disabled ? null : handleRef}
          className={`${styles.handle} ${theme === 'dark' ? styles.handleDark : styles.handleLight}`}
          style={{}}
        />
        {children}
      </div>
    </>
  );
}

function DropPlaceholder({column}: { column: string }) {
  const {ref} = useDroppable({
    id: 'empty_' + column,
    data: {column},
  });

  return (
    <div
      ref={ref}
      style={{
        padding: 20,
        border: '2px dashed gray',
        minHeight: 80,
      }}
    >
      Släpp artiklar här
    </div>
  );
}

interface Props {
  articles: Article[] | null;
  onAddMany?: (articles: Article[]) => void;
  onUpsert?: (article: Article) => void;
  onDelete?: (article: Article) => void;
  isNewCase?: boolean;
  readonly?: boolean;
  selectEditMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (id?: string) => void;
  renderExtraInfo?: boolean;
  renderDeliveryStatus?: boolean;
  pluralVerb?: string;
  maxArticles?: number;
  openInfoIds?: string[];
  onToggleOpenInfo?: (id?: string) => void;
  enableDrag?: boolean;
  groupByArticleType?: boolean;
  showArticleTypeTitle?: boolean;
}

export const CaseArticles: React.FC<Props> = ({
                                                articles,
                                                onAddMany,
                                                onUpsert,
                                                onDelete,
                                                isNewCase,
                                                readonly,
                                                selectEditMode,
                                                selectedIds = [],
                                                onToggleSelect,
                                                renderExtraInfo,
                                                renderDeliveryStatus,
                                                maxArticles,
                                                pluralVerb = 'artiklar',
                                                openInfoIds = [],
                                                onToggleOpenInfo,
                                                enableDrag = false,
                                                groupByArticleType= false,
                                                showArticleTypeTitle = false
                                              }) => {

  const addBatchArticles = (articles: M3ArticleWithQuantity[]) => {
    const startIndex = Math.max((articleItems["Order"] ?? []).length- 1, 0); // lowest value = 0

    if (onAddMany) {
      onAddMany(
        articles.map(((article, index) => ({
          id: generateRandomString(20),
          articleId: article.m3Id ?? '',
          productName: article.name,
          productDescription: article.description,
          quantity: article.quantity,
          articleType: ArticleType.Order,
          sortIndex: startIndex + index
        }))),
      );
    }
  };


  const CaseEditArticleForm: React.FC<{ article?: Article; articleReadOnly?: boolean; disabled?: boolean }> = ({
                                                                                                                 article,
                                                                                                                 articleReadOnly,
                                                                                                                 disabled,
                                                                                                               }) => {

    const {fetchResult: fetchResultImage, image} = findImageForArticle(article?.articleId);

    const [productName, setProductName] = useState<string>(article?.productName ?? '');
    const [productDescription, setProductDescription] = useState<string>(article?.productDescription ?? '');
    const [articleId, setArticleId] = useState<string>(article?.articleId ?? '');

    const [quantity, setQuantity] = useState(article?.quantity ?? 1);
    const [deliveryStatus, setDeliveryStatus] = useState(article?.deliveryStatus ?? DeliveryStatus.Unspecified);

    const {fetchResult, article: newArticle} = useArticle({articleId});
    const hasM3Article = useMemo(() => fetchResult === FetchResult.Found, [fetchResult]);

    const [localOpenInfoIds, setLocalOpenInfoIds] = useState<string[]>([]);
    const effectiveOpenInfoIds = onToggleOpenInfo ? openInfoIds : localOpenInfoIds;
    const isInfoOpen = article ? effectiveOpenInfoIds.includes(article.id!) : false;

    const effectiveToggleOpenInfo = (id?: string) => {
      if (!id) return;

      if (onToggleOpenInfo) {
        onToggleOpenInfo(id);
      } else {
        setLocalOpenInfoIds(prev =>
          prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id]
        );
      }
    };

    const isValidArticleEntry = useMemo(() => {
      let result: boolean;
      if (hasM3Article) {
        result = articleId === newArticle?.m3Id && productName === newArticle?.name && productDescription === newArticle?.description;
      } else {
        result = !articleId && !!productName;
      }
      return result;
    }, [articleId, productName, productDescription, newArticle, hasM3Article]);

    useEffect(() => {
      if (hasM3Article) {
        setArticleId(newArticle?.m3Id || article?.articleId || '');
        setProductName(newArticle?.name || article?.productName || '');
        setProductDescription(newArticle?.description || article?.productDescription || '');
      } else {
        setProductName(article?.productName || '');
        setProductDescription(article?.productDescription || '');
      }
    }, [articleId, newArticle, article, hasM3Article]);

    const isSelected = article ? selectedIds.includes(article.id!) : false;

    const clearForm = () => {
      setProductName('');
      setArticleId('');
      setQuantity(1);
      setDeliveryStatus(DeliveryStatus.Unspecified);
    };

    const closeEditMode = () => setEditArticle(undefined);

    const upsertArticle = () => {
      onUpsert?.({...article, productName, productDescription, articleId, quantity, deliveryStatus});
      if (!article) {
        clearForm();
      }
      closeEditMode();
    };

    const deleteArticle = () => {
      if (article == null) return;

      onDelete?.(article);
      closeEditMode();
    };

    const onChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = Number(e.currentTarget.value);

      if (isNaN(value) || value < 1) {
        value = 1;
      }

      setQuantity(value);
    };

    const toggleArticleInfo = () => {
      effectiveToggleOpenInfo(article?.id);
    };

    const handleDeliveryStatusUpdate = (event: SelectChangeEvent<DeliveryStatus>) => {
      setDeliveryStatus(event.target.value as DeliveryStatus);
    };

    const deliveryStatusValues = [DeliveryStatus.Unspecified, DeliveryStatus.Ordered, DeliveryStatus.InStock].map(value => (
      <MenuItem key={value} value={value}>
        {DeliveryStatusConverter.ToName(value)}
      </MenuItem>
    ));

    const hasNotEdited =
      (article == null && !productName && !articleId) ||
      (article &&
        article.productName === productName &&
        article.productDescription === productDescription &&
        article.articleId === articleId &&
        article.quantity === quantity);

    const saveIsDisabled = disabled || hasNotEdited || !isValidArticleEntry;

    const saveOnEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !saveIsDisabled) {
        upsertArticle();
      }
    };

    const articleDataComponent = (
      <>
        {articleReadOnly ? (
          <>
            <div>
              {selectEditMode ? (
                <>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(article?.id)}
                    onClick={e => e.stopPropagation()}
                    className={styles.check}
                  />
                  <span className={styles.articleHeader}>{articleId}</span>
                  <span className={styles.articleHeaderSeparator}>|</span>
                  {/*<br />*/}
                  <strong>{productName}</strong>

                </>
              ) : (
                <>
                  <span className={styles.articleHeader}>{articleId}</span>
                  <span className={styles.articleHeaderSeparator}>|</span>
                  <strong>{productName}</strong>
                </>
              )}


            </div>
            <Typography variant="body2">{productDescription}</Typography>
          </>
        ) : (
          <>
            <CaseTextField
              name="articleNumber"
              value={articleId}
              setValue={setArticleId}
              label="M-nummer"
              disabled={disabled}
              className={styles.field}
              onKeyDown={saveOnEnter}
            />
            <CaseTextField
              name="productName"
              value={productName}
              setValue={setProductName}
              label="Produktnamn"
              disabled={disabled || !!articleId}
              className={styles.field}
              onKeyDown={saveOnEnter}
            />
            <CaseTextField
              name="productDescription"
              value={productDescription}
              setValue={setProductDescription}
              label="Produktbeskrivning"
              disabled={disabled || !!articleId}
              className={styles.field}
              onKeyDown={saveOnEnter}
            />
          </>
        )}
      </>
    );

    const articleImageComponent = (
      <>
        {!article && (
          <>
            <IconButton onClick={() => setModalIsVisible(true)} disabled={disabled}>
              <Search/>
            </IconButton>
            <IconButton size="small" onClick={upsertArticle} disabled={saveIsDisabled}>
              <Add/>
            </IconButton>
          </>
        )}
        {hasM3Article && fetchResultImage === FetchResult.Found && image && (
          <EnlargableImage image={image} title={productName} className={styles.avatar}/>
        )}
        {hasM3Article && fetchResultImage === FetchResult.NotFound && (
          <Tooltip title="Artikeln har ingen bild kopplad">
            <NoPhotography/>
          </Tooltip>
        )}
        {article && !hasM3Article && (
          <Tooltip title="Artikeln kunde inte hittas">
            <QuestionMark/>
          </Tooltip>
        )}
      </>
    );

    const articleInformationButtonComponent = (
      <>
        {newArticle && (
          <>
            <IconButton size="small" onClick={toggleArticleInfo}>
              <Info/>
            </IconButton>
          </>
        )}
      </>
    );

    const articleInformationComponent = (
      <>
        {isInfoOpen && newArticle && (
          <CaseArticleInfo article={newArticle}/>
        )}
      </>
    );

    const articleQuantityComponent = articleReadOnly ? (
      <>Antal: {quantity}</>
    ) : (
      article && (
        <div className={styles.buttonContainer}>
          <TextField
            name="quantity"
            type="number"
            value={quantity}
            onChange={onChangeQuantity}
            label="Antal"
            disabled={disabled}
            className={styles.shortField}
            // select all text when focused
            onFocus={e => e.target.select()}
          />
          <div className={styles.spacer}/>
          <IconButton size="small" onClick={upsertArticle} disabled={saveIsDisabled}>
            <Done/>
          </IconButton>
          <IconButton size="small" onClick={deleteArticle} disabled={disabled}>
            <Delete/>
          </IconButton>
        </div>
      )
    );

    const articleEditComponent = (
      <>
        {articleReadOnly && article && (
          <IconButton size="small" onClick={() => setEditArticle(article.id)} disabled={disabled}>
            <Edit/>
          </IconButton>
        )}
        {!articleReadOnly && article && (
          <IconButton size="small" onClick={closeEditMode} disabled={disabled}>
            <Close/>
          </IconButton>
        )}
      </>
    );

    return (
      <>
        <Paper variant="outlined" className={styles.articleCard}>
          <div className={styles.article}>
            <div className={styles.articleOverviewEdit}>
              <div className={styles.articleData}>{articleDataComponent}</div>
              <div className={styles.articleEditRow}>
                <div className={styles.articleQuantity}>{articleQuantityComponent}</div>
                <div>{articleInformationButtonComponent}</div>
                <div>{articleEditComponent}</div>
              </div>
            </div>
            <div className={styles.articleImage}>{articleImageComponent}</div>
          </div>
          {articleInformationComponent}
        </Paper>
        {renderExtraInfo && renderDeliveryStatus && (
          <>
            {articleReadOnly ? (
              DeliveryStatusConverter.ToName(deliveryStatus)
            ) : (
              <>
                <InputLabel shrink>Leveransstatus</InputLabel>
                <Select value={deliveryStatus} onChange={handleDeliveryStatusUpdate} className={styles.field}
                        disabled={disabled}>
                  {deliveryStatusValues}
                </Select>
              </>
            )}
          </>
        )}
      </>
    );
  };

  const sortedArticles = [...(articles ?? [])].sort((a, b) => {
    if (a.articleType !== b.articleType) {
      return (a.articleType ?? 0) - (b.articleType ?? 0);
    }
    return (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
  });

  const orderArticles =
    sortedArticles?.filter(a => (a.articleType ?? ArticleType.Order) === ArticleType.Order) ?? [];

  const optionalArticles =
    sortedArticles?.filter(a => a.articleType === ArticleType.Optional) ?? [];

  const allArticles = sortedArticles ?? [];

  useEffect(() => {
    if (groupByArticleType) {
      setArticleItems({
        Order: withDragId(orderArticles),
        Optional: withDragId(optionalArticles),
      });
    } else {
      setArticleItems({
        All: withDragId(allArticles),
      });
    }
  }, [articles, groupByArticleType]);

  const isReadOnly = isNewCase || readonly;
  const palette = useTheme();
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [editArticle, setEditArticle] = useState<string>();

  const articleIds = articles?.map(a => a.articleId).filter(a => a !== undefined);
  const {findImageForArticle} = useArticleImages({articleIds, shouldFetch: true});
  const canAddArticles = maxArticles == null || (articles?.length ?? 0) < maxArticles;

  const editableFinalRow = canAddArticles ? (
    <Box className={styles.fullWidthArticle}>
      <CaseEditArticleForm disabled={editArticle != null}/>
    </Box>
  ) : (
    <div className={styles.row}>Maximalt antal {pluralVerb} tillagda i beställningen.</div>
  );

  const finalRow = !isReadOnly ? (
    editableFinalRow
  ) : (
    <div>{isNewCase ? `Du kan lägga till ${pluralVerb} efter beställningen är sparad` : `Inga ${pluralVerb} tillagda`}</div>
  );

  type ArticleColumn = "Order" | "Optional" | "All";;

  const withDragId = (articles: Article[] = []): ArticleDnD[] =>
    articles.map(a => ({
      ...a,
      dragId: a.id ?? generateRandomString(20),
    }));

  const [articleItems, setArticleItems] =
    useState<Partial<Record<ArticleColumn, ArticleDnD[]>>>(
      groupByArticleType
        ? {
          Order: withDragId(orderArticles),
          Optional: withDragId(optionalArticles),
        }
        : {
          All: withDragId(allArticles),
        }
    );

  const [columnArticles] =
    useState<ArticleColumn[]>(
      groupByArticleType ? ["Order", "Optional"] : ["All"]
    );


  function moveArticles(
    items: Partial<Record<ArticleColumn, ArticleDnD[]>>,
    event: any
  ): Partial<Record<ArticleColumn, ArticleDnD[]>> {
    const {source, target} = event.operation;
    if (!source || !target) return items;

    const sourceColumn = source.group as ArticleColumn;
    const targetColumn = (target.group ?? target.data?.column) as ArticleColumn;

    const sourceIndex = source.index;
    const targetIndex = target.index;

    const sourceItems = [...(items[sourceColumn] ?? [])];

    const targetItems =
      sourceColumn === targetColumn
        ? sourceItems
        : [...(items[targetColumn] ?? [])];

    const [moved] = sourceItems.splice(sourceIndex, 1);
    targetItems.splice(targetIndex, 0, moved);

    return {
      ...items,
      [sourceColumn]: sourceItems,
      [targetColumn]: targetItems
    };
  }

  function normalizeSortIndex(items: Partial<Record<ArticleColumn, ArticleDnD[]>>) {
    const result: Partial<Record<ArticleColumn, ArticleDnD[]>> = {...items};

    for (const column of Object.keys(result) as ArticleColumn[]) {
      result[column] = (result[column] ?? []).map((a, i) => ({
        ...a,
        sortIndex: i,
        articleType: column === "Order" ? ArticleType.Order : ArticleType.Optional
      }));
    }
    return result;
  }

  return (
    <Box>
      {renderExtraInfo && renderDeliveryStatus && <div>Leveransstatus</div>}

      <DragDropProvider
        onDragOver={(event) => {
          const {source, target} = event.operation;

          if (source?.type !== "article") return;

          setArticleItems(items => moveArticles(items, event));
        }}
        onDragEnd={(event) => {
          const {source, target} = event.operation;

          if (source?.type !== "article") return;

          setArticleItems((items) => {
            const moved = moveArticles(items, event);
            const normalized = normalizeSortIndex(moved);

            let articles = groupByArticleType
              ? [...(normalized.Order ?? []), ...(normalized.Optional ?? [])]
              : normalized.All;

            if (articles) {
              onAddMany?.(articles);
            }

            return normalized;
          });
        }}
      >
        <div>
          {columnArticles.map((column, columnIndex) => (
            <>
              {groupByArticleType && showArticleTypeTitle && (
                <Typography variant="h6" sx={{marginTop: 3, width: '100%'}}>
                  {column === 'Order' ? 'Beställningsartiklar' : 'Tillvalsartiklar'}
                </Typography>
              )}
              <ArticleColumn
                key={column}
              >
                {(articleItems[column] ?? []).length === 0 ? (
                  <DropPlaceholder column={column} />
                ) : (
                  <>
                    {(articleItems[column] ?? [])
                      .filter((article): article is ArticleDnD => !!article)
                      .map((article, index) => (
                        <DraggableArticle
                        key={article.articleId || article.dragId}
                        id={article.articleId || article.dragId}
                        index={index}
                        column={column}
                        disabled={!!!enableDrag}
                        theme={palette.palette.mode}
                      >
                        <CaseEditArticleForm
                          article={article}
                          articleReadOnly={editArticle !== article.id}
                        />
                      </DraggableArticle>
                    ))}
                  </>
                )}
              </ArticleColumn>
            </>
          ))}
        </div>
      </DragDropProvider>

      {finalRow}

      <M3SearchModal selectMultiple onSave={addBatchArticles} isVisible={modalIsVisible}
                     onClose={() => setModalIsVisible(false)}/>
    </Box>
  );
};
